import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CatSideMenu } from "@cat-digital-blocks/react/bedrock/components/SideMenu";
import { CatSideMenuItem } from "@cat-digital-blocks/react/bedrock/components/SideMenuItem";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatIconSettings } from "@cat-digital-blocks/react/bedrock/components/Icons/Settings";
import { CatIconBusiness } from "@cat-digital-blocks/react/bedrock/components/Icons/Business";
import styles from "./SideMenu.module.scss";
import {
  MAIN_MENU_ITEMS,
  ADMIN_MENU_ITEMS,
} from "@/shared/configs/menu.config";
import { ARIA_LABELS } from "@/shared/constants";
import type { NavigationItem } from "@/types";

interface SideMenuProps {
  /** User's roles for the current dealer */
  userRoles?: string[];
}

/**
 * Side navigation menu component using CAT Digital Blocks
 * Displays main navigation items and role-based admin menu with expandable submenus
 */
const SideMenu = ({ userRoles = [] }: SideMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  /**
   * Check if user has required role for menu item
   */
  const hasRequiredRole = useCallback(
    (item: NavigationItem): boolean => {
      if (!item.requiredRole) return true;
      return userRoles.includes(item.requiredRole);
    },
    [userRoles],
  );

  /**
   * Filter admin menu items based on user roles
   */
  const visibleAdminItems = useMemo(() => {
    return ADMIN_MENU_ITEMS.filter((item) => hasRequiredRole(item));
  }, [hasRequiredRole]);

  return (
    <CatSideMenu
      className={styles.sideMenu}
      data-testid="cat-side-menu"
      arialabel={ARIA_LABELS.MAIN_NAVIGATION}
    >
      {/* Main Navigation Items */}
      {MAIN_MENU_ITEMS.map((item: NavigationItem) => (
        <CatSideMenuItem
          key={item.id}
          isCurrent={location.pathname === item.path}
          onBlSidemenuitemSelect={() => item.path && handleNavigate(item.path)}
        >
          {t(item.label)}
          <CatIconSettings slot="before" />
        </CatSideMenuItem>
      ))}

      {/* Administration Menu Items - Role-based with expandable submenus */}
      {visibleAdminItems.map((item: NavigationItem) => {
        const hasChildren = item.children && item.children.length > 0;

        return (
          <CatSideMenuItem
            key={item.id}
            isCurrent={
              !hasChildren && location.pathname.startsWith(item.path || "")
            }
          >
            {t(item.label)}
            <CatIconBusiness slot="before" />
            {hasChildren && (
              <CatList slot="items">
                {item.children
                  ?.filter((child) => hasRequiredRole(child))
                  .map((child) => (
                    <CatListItem
                      key={child.id}
                      isCurrent={location.pathname.startsWith(child.path || "")}
                      onClick={() => child.path && handleNavigate(child.path)}
                    >
                      {t(child.label)}
                    </CatListItem>
                  ))}
              </CatList>
            )}
          </CatSideMenuItem>
        );
      })}
    </CatSideMenu>
  );
};

SideMenu.displayName = "SideMenu";

export default SideMenu;
